import axios from "axios";
import { useMemo, useState } from "react";
import Footer from "components/Footer";
import Header from "components/Header";
import { ProductQuery } from "components/products";
import React, { Fragment } from "react";
import notification from "utilities/notification";

const Contact = () => {
  const { data = [], isLoading, error } = ProductQuery();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    subject: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);

  // Memoize category mapping
  const categoryArr = useMemo(() => {
    const categories = {};
    data.forEach((product) => {
      if (product?.category?.name && product?.category?.id) {
        categories[product.category.name] = product.category.id;
      }
    });
    return categories;
  }, [data]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Email is not valid";
    }
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else if (!phoneRegex.test(formData.phoneNumber)) {
      errors.phoneNumber = "Phone number must be 10 digits";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus(null);
    const errors = validateForm();
    const basePath = process.env.REACT_APP_API_BASE_URL;
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {

      const response = await axios.post(basePath + 'sendEnquiry', {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        subject: formData.subject,
        message: formData.message,
      });

      if (response?.data?.success) {
        notification(
          "success",
          "Thank you for your query! We will get back to you soon."
        );
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          subject: "",
          message: "",
        });
        setSubmissionStatus("success");
      } else {
        notification("error", "Something went wrong. Please try again.");
        setSubmissionStatus("error");
      }
    } catch (err) {
      notification("error", "Something went wrong. Please try again.");
      setSubmissionStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Fragment>
      <Header />
      <main className="container contact-container my-5">
        <h1 className="text-center mb-4">Contact Us</h1>
        <div className="row">

          {/* Left Column: Contact Info */}
          <div className="col-lg-5 col-md-6 mb-4">
            <h2 className="contact-heading">Get in Touch</h2>
            <p className="contact-subheading">
              For any assistance or queries, feel free to leave a note.
            </p>
            <p className="contact-divider">— or —</p>
            <div className="contact-info">
              <p>
                <strong>Reach us at:</strong>
              </p>
              <address>
                <p>
                  <strong>PRIMESPOT RETAIL</strong>
                </p>
                <p>504 - 5th Floor, Salcon Rasvilas</p>
                <p>Saket District Centre, Sector 6, Pushp Vihar</p>
                <p>New Delhi, 110017, India</p>
              </address>
              <p>
                <strong>Phone:</strong> +91 80805 40679
              </p>
              <p>
                <strong>Email:</strong> care@bcplindia.com
              </p>
              <p>We will respond to your message within 24 hours.</p>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="col-lg-7 col-md-6">
            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              {submissionStatus === "success" && (
                <div className="alert alert-success text-center" role="alert">
                  Thank you for your query! We will get back to you soon.
                </div>
              )}
              {submissionStatus === "error" && (
                <div className="alert alert-danger text-center" role="alert">
                  Something went wrong. Please try again.
                </div>
              )}
              <div className="row">
                <div className="col-md-6 form-group mb-3">
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-control ${formErrors.name ? "is-invalid" : ""
                      }`}
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                  {formErrors.name && (
                    <div className="invalid-feedback">{formErrors.name}</div>
                  )}
                </div>

                <div className="col-md-6 form-group mb-3">
                  <label htmlFor="email" className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${formErrors.email ? "is-invalid" : ""
                      }`}
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                  {formErrors.email && (
                    <div className="invalid-feedback">{formErrors.email}</div>
                  )}
                </div>

                <div className="col-md-6 form-group mb-3">
                  <label htmlFor="phoneNumber" className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    className={`form-control ${formErrors.phoneNumber ? "is-invalid" : ""
                      }`}
                    placeholder="Your Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    required
                  />
                  {formErrors.phoneNumber && (
                    <div className="invalid-feedback">
                      {formErrors.phoneNumber}
                    </div>
                  )}
                </div>

                <div className="col-md-6 form-group mb-3">
                  <label htmlFor="subject" className="form-label">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="form-control"
                    placeholder="Your Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="col-md-12 form-group mb-3">
                  <label htmlFor="message" className="form-label">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-control"
                    rows={4}
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  ></textarea>
                </div>
              </div>
              <div className="text-center mt-3">
                <button
                  type="submit"
                  className="btn btn-dark rounded"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-3">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3505.408796525177!2d77.2203153!3d28.527429899999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce18b0950c389%3A0xebbb242665d51b64!2sSalcon%20Rasvilas!5e0!3m2!1sen!2sin!4v1745646181816!5m2!1sen!2sin"
            width="100%"
            height="250"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </main>

      <div className="container-fluid">
        {isLoading ? (
          <p>Loading footer...</p>
        ) : error ? (
          <p>Error loading footer categories.</p>
        ) : (
          <Footer category={categoryArr} />
        )}
      </div>
    </Fragment>
  );
};

export default Contact;
